import React from 'react';

import { Box, VisuallyHidden } from '@rbilabs/components-library';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { IBaseProps } from '@rbi-ctg/frontend';
import { IItem } from '@rbi-ctg/menu';
import ActionButton from 'components/action-button';
import { brandFont } from 'components/layout/brand-font';
import defaultConfig from 'components/menu-order-wizard/config';
import Accordion from 'components/selection-container/accordion';
import { DialogFooter } from 'components/sticky-footer';
import { AddOnSelections, IAddOnSection, IAddOnSectionOption } from 'hooks/use-cart-add-ons';
import { useFormatCalories } from 'hooks/use-format-calories';
import { useMenuOptionsContext } from 'state/menu-options';
import { useOrderContext } from 'state/order';
import { useUIContext } from 'state/ui';
import { primitive } from 'styles/constants/primitives';
import { getMaxCartQuantity } from 'utils/cart';
import { getItemCalories } from 'utils/menu';
import { escapeQuantity } from 'utils/wizard';

import AddExtrasContentItem from './add-extras-content-item';

interface IAddExtrasProps extends IBaseProps {
  addOnSelections: AddOnSelections;
  addOns: IAddOnSection[];
  addToCart: () => void;
  isEdit: boolean;
  updateQuantityForitem: (
    item: IAddOnSectionOption,
    quantity: number,
    section: IAddOnSection
  ) => void;
  totalPrice: number;
  totalQuantity: number;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  text-align: left;
  background-color: ${primitive.$white};

  & > fieldset {
    border: none;
    flex: 1;
    justify-content: unset;
  }
`;

const SubHeader = styled.span`
  font: ${brandFont.copyTwo};
  text-transform: none;
`;

const ButtonContainer = styled.div`
  width: 100%;
  padding: 16px;
`;

const AddExtras: React.FC<IAddExtrasProps> = ({
  addOns,
  addOnSelections,
  addToCart,
  isEdit,
  updateQuantityForitem,
  totalPrice,
  totalQuantity,
}) => {
  const { formatCurrencyForLocale } = useUIContext();
  const { formatMessage } = useIntl();
  const { pricingFunction } = useMenuOptionsContext();
  const formatCalories = useFormatCalories();
  const { serviceMode } = useOrderContext();
  const maxQuantity = getMaxCartQuantity(serviceMode);

  const getCalorieString = (item: IItem) => formatCalories(getItemCalories(item));

  const getItemMax = (
    itemOptionId: string,
    itemMax: number | null,
    sectionId: string,
    sectionMax: number | null
  ) => {
    const maxForThisItem = escapeQuantity(itemMax ?? maxQuantity, serviceMode);
    if (!sectionMax || sectionMax === 0) {
      return maxForThisItem;
    }

    // How many are we at for the section currently?
    const totalForSection = Object.values(addOnSelections).reduce(
      (total, { sectionId: selectionSectionId, quantity: selectionQuantity }) => {
        return selectionSectionId === sectionId ? total + selectionQuantity : total;
      },
      0
    );

    // Did we hit the sectionMax?
    // If so return the current selection or zero
    if (totalForSection === sectionMax) {
      return addOnSelections[itemOptionId]?.quantity ?? 0;
    }

    // If not continue as normal
    return maxForThisItem;
  };

  return (
    <Container>
      {addOns.map(addOnSection => (
        <Accordion
          key={addOnSection._id}
          title={
            <Box flexDirection="column">
              {addOnSection.name.locale}
              {addOnSection.maxAmount > 0 && (
                <SubHeader>
                  {formatMessage({ id: 'totalChoices' }, { amount: addOnSection.maxAmount })}
                </SubHeader>
              )}
            </Box>
          }
          openOnMount
        >
          <div
            data-testid={`add-on-section-${addOnSection.name.locale}`}
            role="group"
            aria-label={addOnSection.name.locale}
          >
            <VisuallyHidden>{addOnSection.name.locale}</VisuallyHidden>
            {addOnSection?.options?.map(addOnOption => (
              <AddExtrasContentItem
                key={addOnOption._key}
                itemName={addOnOption.option.name.locale}
                itemCalories={getCalorieString(addOnOption.option)}
                index={addOnSelections[addOnOption._key]?.quantity ?? 0}
                onChange={newQuantity => {
                  updateQuantityForitem(addOnOption, newQuantity, addOnSection);
                }}
                itemPrice={pricingFunction(addOnOption.option)}
                itemImage={addOnOption.option.image}
                isInCart={
                  addOnSelections[addOnOption._key]?.data.name.locale ===
                  addOnOption.option.name.locale
                }
                max={getItemMax(
                  addOnOption._key,
                  addOnOption.maxAmount,
                  addOnSection._id,
                  addOnSection.maxAmount
                )}
                maxAmount={addOnSection.maxAmount}
              />
            ))}
          </div>
        </Accordion>
      ))}

      <DialogFooter
        elements={[
          {
            element: (
              <ButtonContainer>
                <ActionButton
                  variant={defaultConfig.buttonVariant}
                  aria-label={formatMessage(
                    { id: 'addQuantityAndItemWithPrice' },
                    {
                      itemName: `Extra${totalQuantity > 1 ? 's' : ''}`,
                      quantity: totalQuantity,
                      price: formatCurrencyForLocale(totalPrice),
                    }
                  )}
                  data-testid="extras-add-to-order-button"
                  perceptible
                  onClick={addToCart}
                  fullWidth
                >
                  {[
                    formatMessage({
                      id: isEdit ? 'updateCart' : 'addToOrder',
                    }),
                    formatCurrencyForLocale(totalPrice),
                  ].join(' — ')}
                </ActionButton>
              </ButtonContainer>
            ),
          },
        ]}
      />
    </Container>
  );
};

export default AddExtras;
